/**
 * The cartoon's sound: four channels on one Web Audio graph.
 *
 * Two are CUES that belong to one shot and start with it — the narration and
 * the effect. Two RUN ON and only act when the file changes — the music, which
 * arrives as one stem per shot cut to butt up against the next (so a change is
 * a gap-free swap, never a fade), and the ambience bed, one looping recording
 * of a place, which fades out and back in at a scene boundary.
 *
 * Web Audio rather than `<audio>` elements because iOS ignores `volume` on
 * media elements, and a bed that cannot sit under the narration is not a bed.
 * The price is the autoplay rule: the context has to be created and resumed
 * inside the tap that starts the cartoon — see {@link CartoonMixer.unlock}.
 *
 * Files are fetched ahead (`prefetch`) and decoded on first use; decoded audio
 * is kept, because a replay would otherwise decode the whole score again.
 */
import { BED_FADE_SEC, type AnimationSegment } from './segments';

/** Mix levels, taken from the Character Editor's Animation tab. */
const LEVEL = { vo: 1, sfx: 0.8, music: 0.45, ambience: 0.35 } as const;

interface Cue {
  url: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

type Channel = keyof typeof LEVEL;

export class CartoonMixer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private bytes = new Map<string, Promise<ArrayBuffer>>();
  private decoded = new Map<string, Promise<AudioBuffer>>();
  private playing: Partial<Record<Channel, Cue>> = {};
  /** Per channel, the request that is allowed to land — a stale decode must not start. */
  private token: Record<Channel, number> = { vo: 0, sfx: 0, music: 0, ambience: 0 };
  /** A narration line waiting for the one still being spoken to finish. */
  private pendingVo: string | null = null;

  /** Start downloading, so a cue is in memory before its shot arrives. */
  prefetch(urls: (string | undefined)[]): void {
    for (const url of urls) {
      if (!url || this.bytes.has(url)) continue;
      this.bytes.set(url, fetch(url).then((r) => {
        if (!r.ok) throw new Error(`${url}: ${r.status}`);
        return r.arrayBuffer();
      }));
    }
  }

  /**
   * Must be called from a user gesture. Safe to call again — also after
   * `dispose()`, which matters under React's development double-mount: the
   * first mount's cleanup disposes, the second mount's tap has to work.
   */
  async unlock(): Promise<void> {
    if (this.ctx && this.ctx.state === 'closed') this.ctx = null;
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.connect(this.ctx.destination);
    }
    try {
      await this.ctx.resume();
    } catch {
      /* the next gesture will try again */
    }
  }

  private buffer(url: string): Promise<AudioBuffer> {
    const ctx = this.ctx;
    if (!ctx) return Promise.reject(new Error('audio not unlocked'));
    let p = this.decoded.get(url);
    if (!p) {
      this.prefetch([url]);
      // Copy: decodeAudioData detaches the buffer it is handed, and a second
      // decode (a replay) would find an empty one.
      p = this.bytes.get(url)!.then((b) => ctx.decodeAudioData(b.slice(0)));
      p.catch(() => this.decoded.delete(url));
      this.decoded.set(url, p);
    }
    return p;
  }

  private stop(channel: Channel, fadeSec = 0): void {
    if (channel === 'vo') this.pendingVo = null;
    const cue = this.playing[channel];
    if (!cue || !this.ctx) return;
    delete this.playing[channel];
    cue.source.onended = null; // stopped by us, not finished: nothing queued may follow
    const now = this.ctx.currentTime;
    try {
      if (fadeSec > 0) {
        cue.gain.gain.setValueAtTime(cue.gain.gain.value, now);
        cue.gain.gain.linearRampToValueAtTime(0, now + fadeSec);
        cue.source.stop(now + fadeSec + 0.05);
      } else {
        cue.source.stop();
      }
    } catch {
      /* already ended */
    }
  }

  private async start(channel: Channel, url: string, loop: boolean, fadeSec = 0): Promise<void> {
    const my = ++this.token[channel];
    let buffer: AudioBuffer;
    try {
      buffer = await this.buffer(url);
    } catch {
      return; // a missing cue is silence, not a broken cartoon
    }
    if (my !== this.token[channel] || !this.ctx || !this.master) return;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;
    const gain = this.ctx.createGain();
    const now = this.ctx.currentTime;
    if (fadeSec > 0) {
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(LEVEL[channel], now + fadeSec);
    } else {
      gain.gain.value = LEVEL[channel];
    }
    source.connect(gain).connect(this.master);
    source.start();
    const cue: Cue = { url, source, gain };
    this.playing[channel] = cue;
    if (channel === 'vo') {
      // The line ended on its own: the one queued behind it may speak now.
      source.onended = () => {
        if (this.playing.vo !== cue) return;
        delete this.playing.vo;
        const next = this.pendingVo;
        this.pendingVo = null;
        if (next) void this.start('vo', next, false);
      };
    }
  }

  /**
   * A new shot took the screen. The cues restart; the two continuous channels
   * act only when their file changes, which is what makes a track run on across
   * shots and a bed run through the cut between them.
   */
  startShot(seg: AnimationSegment): void {
    if (!this.ctx) return;
    // THE NARRATION RUNS ON. A line is written for its shot but not cut to it
    // (the recorded intro is 8 s over a 5 s shot; a spoken name makes a line
    // longer still), and a voice that stops mid-sentence at every cut is
    // worse than one that finishes a beat late. So a shot's line starts when
    // the shot does — unless the previous line is still being spoken, in
    // which case it waits its turn and the drift is absorbed by the next
    // shot that has slack.
    this.stop('sfx');
    this.token.sfx += 1;
    if (seg.sfxSrc) void this.start('sfx', seg.sfxSrc, false);
    if (seg.voiceOverSrc) {
      if (this.playing.vo) this.pendingVo = seg.voiceOverSrc;
      else { this.token.vo += 1; void this.start('vo', seg.voiceOverSrc, false); }
    }

    if (this.playing.music?.url !== seg.musicSrc) {
      this.stop('music');
      this.token.music += 1;
      if (seg.musicSrc) void this.start('music', seg.musicSrc, false);
    }
    if (this.playing.ambience?.url !== seg.ambienceSrc) {
      const had = !!this.playing.ambience;
      this.stop('ambience', had ? BED_FADE_SEC : 0);
      this.token.ambience += 1;
      if (seg.ambienceSrc) void this.start('ambience', seg.ambienceSrc, true, BED_FADE_SEC);
    }
  }

  /** Suspend the clock: every channel holds where it is. */
  pause(): void {
    void this.ctx?.suspend().catch(() => {});
  }

  resume(): void {
    void this.ctx?.resume().catch(() => {});
  }

  stopAll(): void {
    for (const ch of Object.keys(LEVEL) as Channel[]) {
      this.stop(ch);
      this.token[ch] += 1;
    }
  }

  dispose(): void {
    this.stopAll();
    this.decoded.clear();
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.master = null;
  }
}
